import dagre from 'dagre'

/**
 * Shared dagre layout for Plant Viewer flow DAGs (subline FEEDS,
 * equipment CONNECTS_TO). Returns top-left node positions and SVG
 * bezier paths for edges; callers own tile rendering.
 */

export type RankDirection = 'LR' | 'TB'

export interface FlowDagNodeLayout {
  id: string
  x: number
  y: number
}

export interface FlowDagEdgeLayout {
  key: string
  path: string
}

export interface FlowDagLayout {
  width: number
  height: number
  nodes: FlowDagNodeLayout[]
  edges: FlowDagEdgeLayout[]
}

/** Compact enough that typical branching step graphs fit without dominating the page. */
export const FLOW_DAG_NODE_W = 200
/** Fits a two-line wrapped title + one subtitle line inside DAG FlowTiles. */
export const FLOW_DAG_NODE_H = 60
const CANVAS_PAD = 8

/** Drop self-loops, unknown endpoints, and duplicate directed pairs. */
export function filterFlowEdges<T extends { source: string; target: string }>(
  nodeIds: Iterable<string>,
  edgesIn: readonly T[]
): T[] {
  const idSet = new Set(nodeIds)
  const seenPairs = new Set<string>()
  const edges: T[] = []
  for (const edge of edgesIn) {
    if (!idSet.has(edge.source) || !idSet.has(edge.target)) continue
    if (edge.source === edge.target) continue
    const pair = `${edge.source}\u0000${edge.target}`
    if (seenPairs.has(pair)) continue
    seenPairs.add(pair)
    edges.push(edge)
  }
  return edges
}

interface RoutedEdge {
  path: string
  minX: number
  minY: number
  maxX: number
  maxY: number
}

/**
 * Route an edge from the side of each node that faces the other.
 * Avoids assuming rankdir order — back-edges / same-rank links otherwise
 * push cubic control points far outside the node bounding box and get
 * clipped by the section overflow.
 */
export function flowDagEdgePath(
  source: FlowDagNodeLayout,
  target: FlowDagNodeLayout,
  nodeW: number,
  nodeH: number
): RoutedEdge {
  const scx = source.x + nodeW / 2
  const scy = source.y + nodeH / 2
  const tcx = target.x + nodeW / 2
  const tcy = target.y + nodeH / 2
  const dx = tcx - scx
  const dy = tcy - scy

  if (Math.abs(dy) >= Math.abs(dx)) {
    const downward = dy >= 0
    const sx = scx
    const sy = downward ? source.y + nodeH : source.y
    const tx = tcx
    const ty = downward ? target.y : target.y + nodeH
    const bend = Math.max(12, Math.abs(ty - sy) / 2)
    const c1y = sy + (downward ? bend : -bend)
    const c2y = ty + (downward ? -bend : bend)
    return {
      path: `M ${sx} ${sy} C ${sx} ${c1y}, ${tx} ${c2y}, ${tx} ${ty}`,
      minX: Math.min(sx, tx),
      maxX: Math.max(sx, tx),
      minY: Math.min(sy, ty, c1y, c2y),
      maxY: Math.max(sy, ty, c1y, c2y)
    }
  }

  const rightward = dx >= 0
  const sx = rightward ? source.x + nodeW : source.x
  const sy = scy
  const tx = rightward ? target.x : target.x + nodeW
  const ty = tcy
  const bend = Math.max(16, Math.abs(tx - sx) / 2)
  const c1x = sx + (rightward ? bend : -bend)
  const c2x = tx + (rightward ? -bend : bend)
  return {
    path: `M ${sx} ${sy} C ${c1x} ${sy}, ${c2x} ${ty}, ${tx} ${ty}`,
    minX: Math.min(sx, tx, c1x, c2x),
    maxX: Math.max(sx, tx, c1x, c2x),
    minY: Math.min(sy, ty),
    maxY: Math.max(sy, ty)
  }
}

export function computeFlowDagLayout(
  nodeIds: string[],
  edgesIn: Array<{ source: string; target: string }>,
  rankdir: RankDirection,
  nodeW: number = FLOW_DAG_NODE_W,
  nodeH: number = FLOW_DAG_NODE_H
): FlowDagLayout {
  const edges = filterFlowEdges(nodeIds, edgesIn)

  const graph = new dagre.graphlib.Graph()
  graph.setDefaultEdgeLabel(() => ({}))
  graph.setGraph({
    rankdir,
    nodesep: 12,
    ranksep: rankdir === 'LR' ? 40 : 28,
    marginx: CANVAS_PAD,
    marginy: CANVAS_PAD
  })
  for (const id of nodeIds) graph.setNode(id, { width: nodeW, height: nodeH })
  for (const edge of edges) graph.setEdge(edge.source, edge.target)
  dagre.layout(graph)

  const rawNodes: FlowDagNodeLayout[] = nodeIds.map((id) => {
    const pos = graph.node(id)
    return { id, x: pos.x - nodeW / 2, y: pos.y - nodeH / 2 }
  })
  const rawById = new Map(rawNodes.map((n) => [n.id, n]))

  // Probe routes once so back-edge control points contribute to the origin shift.
  let originX = CANVAS_PAD
  let originY = CANVAS_PAD
  for (const node of rawNodes) {
    originX = Math.min(originX, node.x)
    originY = Math.min(originY, node.y)
  }
  for (const edge of edges) {
    const source = rawById.get(edge.source)
    const target = rawById.get(edge.target)
    if (!source || !target) continue
    const routed = flowDagEdgePath(source, target, nodeW, nodeH)
    originX = Math.min(originX, routed.minX)
    originY = Math.min(originY, routed.minY)
  }
  const shiftX = CANVAS_PAD - originX
  const shiftY = CANVAS_PAD - originY

  const nodes = rawNodes.map((n) => ({ id: n.id, x: n.x + shiftX, y: n.y + shiftY }))
  const nodeById = new Map(nodes.map((n) => [n.id, n]))

  let width = CANVAS_PAD
  let height = CANVAS_PAD
  for (const node of nodes) {
    width = Math.max(width, node.x + nodeW)
    height = Math.max(height, node.y + nodeH)
  }

  const dagEdges: FlowDagEdgeLayout[] = edges.map((edge) => {
    const source = nodeById.get(edge.source)
    const target = nodeById.get(edge.target)
    const key = `${edge.source}->${edge.target}`
    if (!source || !target) return { key, path: '' }
    const routed = flowDagEdgePath(source, target, nodeW, nodeH)
    width = Math.max(width, routed.maxX)
    height = Math.max(height, routed.maxY)
    return { key, path: routed.path }
  })

  return {
    width: width + CANVAS_PAD,
    height: height + CANVAS_PAD,
    nodes,
    edges: dagEdges
  }
}
